import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Section from '../common/Section';
import SkillCard from '../common/SkillCard';
import ParallaxText from '../ui/ParallaxText';
import { SKILLS_DATA } from '../../utils/constants';

const Skills: React.FC = () => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    return (
        <Section id="skills" className="relative overflow-hidden bg-gradient-to-b from-white to-sky-50/50">
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden">
                <motion.div
                    className="absolute top-10 right-10 w-64 h-64 bg-sky-300/20 rounded-full filter blur-3xl"
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.5, 0.3],
                    }}
                    transition={{ duration: 8, repeat: Infinity }}
                />
                <motion.div
                    className="absolute bottom-10 left-10 w-96 h-96 bg-purple-300/20 rounded-full filter blur-3xl"
                    animate={{
                        scale: [1, 1.1, 1],
                        opacity: [0.3, 0.4, 0.3],
                    }}
                    transition={{ duration: 10, repeat: Infinity }}
                />
            </div>

            <div className="relative z-10">
                <ParallaxText offset={30}>
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="h2-display text-slate-900 mb-4">
                            <span className="text-gradient">Skills & Expertise</span>
                        </h2>
                        <div className="w-24 h-1 mx-auto bg-gradient-to-r from-sky-400 to-purple-400 rounded-full mt-6" />
                    </motion.div>
                </ParallaxText>

                {/* Skills Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                    {SKILLS_DATA.map((skill, index) => (
                        <motion.div
                            key={skill.category}
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{
                                duration: 0.6,
                                delay: index * 0.1,
                                ease: [0.19, 1, 0.22, 1],
                            }}
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => setHoveredIndex(null)}
                            className="relative"
                        >
                            {/* Glow effect on hover */}
                            <AnimatePresence>
                                {hoveredIndex === index && (
                                    <motion.div
                                        className="absolute inset-0 bg-gradient-to-br from-sky-400/20 to-purple-400/20 rounded-xl filter blur-xl"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.3 }}
                                    />
                                )}
                            </AnimatePresence>

                            <SkillCard skill={skill} index={index} />
                        </motion.div>
                    ))}
                </div>

                {/* Skills Progress Bars */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="mt-16 max-w-4xl mx-auto"
                >
                    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-sky-100">
                        <h3 className="text-xl font-bold text-slate-900 mb-6">Technical Proficiency</h3>

                        <div className="space-y-6">
                            {[
                                { name: 'UI/UX Design', level: 90, color: 'from-sky-400 to-sky-500' },
                                { name: 'Frontend Development', level: 85, color: 'from-purple-400 to-pink-400' },
                                { name: '3D Design & Metaverse', level: 80, color: 'from-cyan-400 to-sky-400' },
                                { name: 'Server Management', level: 75, color: 'from-pink-400 to-purple-400' },
                            ].map((skill, index) => (
                                <motion.div
                                    key={skill.name}
                                    initial={{ opacity: 0, x: -30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                >
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm font-medium text-slate-700">{skill.name}</span>
                                        <span className="text-sm text-slate-500">{skill.level}%</span>
                                    </div>
                                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                        <motion.div
                                            className={`h-full bg-gradient-to-r ${skill.color} rounded-full`}
                                            initial={{ width: 0 }}
                                            whileInView={{ width: `${skill.level}%` }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 1, delay: 0.2 + index * 0.1, ease: 'easeOut' }}
                                        />
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </motion.div>
            </div>
        </Section>
    );
};

export default Skills;